import { api } from '@organization/api'
import { CurrentOrganizationUser } from '@organization/api/requests'
import { LOGIN_URL, MFA_AUTH_ENABLE } from '@organization/app-constants'
import { ChangePasswordModal } from '@organization/features/authentication/components/ChangePasswordModal'
import { useAppConfig } from '@organization/hooks'
import { organizationI18n } from '@organization/i18n'
import { MFAPurpose } from '@organization/models'
import { paths } from '@organization/router'
import { useSessionContext } from '@shared/hooks'
import { allLangs, useLocales } from '@shared/locales'
import { LanguageSwitcherProps } from '@shared/types'
import { showApiErrors } from '@shared/utils'
import { clearLocalStorage } from '@shared/utils/getToken'
import { useMutation } from '@tanstack/react-query'
import { App, MenuProps } from 'antd'
import {
  Book,
  Category,
  Key,
  Lock1,
  LogoutCurve,
  NotificationBing,
  PathTool,
  People,
  User
} from 'iconsax-react'
import { PropsWithChildren, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Layout } from '../Layout'
import { updateMenuPaths } from '../utils'

export const AppLayout = ({ children }: PropsWithChildren) => {
  const { user, isAdmin, profile } = useSessionContext()
  const { modal } = App.useApp()

  const { currentLang, onChangeLang } = useLocales()

  const [mfaAccessToken, setMfaAccessToken] = useState<string>()

  const { t } = useTranslation('organization', {
    i18n: organizationI18n,
    bindI18n: 'languageChanged languageChanging'
  })

  const navigate = useNavigate()

  const { mutate } = useMutation({
    mutationFn: api.auth.logout,

    onSettled: (_, e) => {
      clearLocalStorage()
      navigate(LOGIN_URL)

      if (e) {
        showApiErrors(e)
      }
    }
  })

  const {
    mutateAsync: changePasswordRequest,
    isPending: isChangePasswordRequestPending
  } = useMutation({
    mutationFn: api.account.changePasswordRequest,
    onSuccess: (data) => {
      if (data?.mfa_access_token) {
        setMfaAccessToken(data.mfa_access_token)

        sendOtp({
          __mfaAccessToken: data.mfa_access_token,
          purpose: MFAPurpose.CHANGE_PASSWORD
        })
      }
    },
    onError: (e) => {
      showApiErrors(e)
    }
  })

  const {
    mutate: sendOtp,
    data: sendOtpData,
    reset: resetSendOtp
  } = useMutation({
    mutationFn: api.mfa.sendOtp,
    onError: (e) => {
      showApiErrors(e)
    }
  })

  const appConfig = useAppConfig()

  const onCloseChangePasswordModal = () => {
    setMfaAccessToken(undefined)
    resetSendOtp()
  }

  const updatedMenuItems = appConfig
    ? updateMenuPaths(appConfig?.menu, appConfig?.key, appConfig?.forceToken)
    : []

  const handleChangePasswordClick = () => {
    modal.confirm({
      title: t('menu.change_password'),
      content: t('auth.change_password_confirm_description'),
      okText: t('button.confirm'),
      cancelText: t('button.cancel'),
      onOk: async () => await changePasswordRequest(),
      okButtonProps: {
        loading: isChangePasswordRequestPending
      }
    })
  }

  const menuLeft = [
    // TODO: add dashboard back if needed with widgets
    // {
    //   key: 'dashboard',
    //   name: t('menu.dashboard'),
    //   path: '/dashboard',
    //   icon: <Home size={18} />
    // },

    !appConfig.forceToken
      ? [
          {
            key: 'applications',
            name: t('menu.applications'),
            path: '/applications',
            icon: <Category size={18} />
          }
        ]
      : [],

    ...(MFA_AUTH_ENABLE || isAdmin
      ? [
          {
            key: 'logs',
            name: t('menu.logs'),
            path: '/logs',
            icon: <Book size={18} />,
            hideChildrenInMenu: true,
            children: [
              {
                key: 'logs-mfa',
                name: t('menu.logs_mfa'),
                path: '/logs/mfa'
              },
              ...(isAdmin
                ? [
                    {
                      key: 'logs',
                      name: t('menu.logs_applications'),
                      path: '/logs/applications'
                    }
                  ]
                : [])
            ]
          }
        ]
      : []),

    ...(isAdmin
      ? [
          {
            key: 'notifications',
            name: t('menu.notifications'),
            path: '/notifications',
            icon: <NotificationBing size={18} />
          },

          ...(MFA_AUTH_ENABLE
            ? [
                {
                  key: 'lockouts',
                  name: t('menu.lockouts_users'),
                  path: '/lockouts',
                  icon: <Lock1 size={18} />
                }
              ]
            : []),

          ...(MFA_AUTH_ENABLE
            ? [
                {
                  name: t('menu.configs_mfa'),
                  key: paths.configsMFAPage.root,
                  path: paths.configsMFAPage.root,
                  icon: <Key size={18} />
                }
              ]
            : []),

          {
            key: 'people',
            name: t('menu.people'),
            icon: <People size={18} />,
            children: [
              {
                key: 'users',
                name: t('menu.people_intact'),
                path: '/people/users',
                hideChildrenInMenu: true,
                children: [
                  {
                    key: 'users',
                    name: t('menu.users'),
                    path: '/people/users'
                  },
                  {
                    key: 'teams',
                    name: t('menu.teams'),
                    path: '/people/teams'
                  }
                ]
              },
              {
                key: 'staff',
                name: t('menu.people_partners'),
                path: '/staff/partners/companies-management',
                hideChildrenInMenu: true,
                children: [
                  {
                    key: 'agents',
                    name: t('menu.agents'),
                    path: '/staff/partners/agents'
                  },
                  {
                    key: 'teams',
                    name: t('menu.teams'),
                    path: '/staff/partners/teams'
                  },
                  {
                    key: 'media',
                    name: t('menu.media'),
                    path: '/staff/partners/media'
                  }
                ]
              }
            ]
          }
        ]
      : [])
  ]

  const profileMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      label: t('menu.profile'),
      icon: <User size={14} />,
      onClick: () => navigate(paths.profile.root)
    },
    ...(isAdmin
      ? [
          {
            key: 'theme',
            label: t('menu.theme'),
            icon: <PathTool size={14} />,
            onClick: () => navigate(paths.theme)
          }
        ]
      : []),
    ...(MFA_AUTH_ENABLE
      ? [
          {
            key: 'change-password',
            label: t('menu.change_password'),
            icon: <Lock1 size={14} />,
            onClick: handleChangePasswordClick
          }
        ]
      : []),
    {
      key: 'logout',
      label: t('menu.logout'),
      icon: <LogoutCurve size={14} />,
      onClick: () => mutate()
    }
  ]

  const menuItems =
    updatedMenuItems.length === 0
      ? menuLeft
      : [...menuLeft.slice(0, 1), ...updatedMenuItems]

  const languages: LanguageSwitcherProps = {
    currentLang,
    languages: allLangs,
    onLanguageChange: onChangeLang
  }

  return (
    <Layout
      profileMenuItems={profileMenuItems}
      organizationData={user as unknown as CurrentOrganizationUser}
      menu={menuItems}
      showTitle={false}
      languages={languages}
      selectedKeys={appConfig.selectedKeys}
      hiddenMenuExtra={!!appConfig.forceToken}
      brokerName={profile?.broker?.name}
    >
      {children}

      {sendOtpData?.expires_in && (
        <ChangePasswordModal
          open
          onCancel={onCloseChangePasswordModal}
          mfaAccessToken={mfaAccessToken}
          expireOtpCode={Number(sendOtpData?.expires_in)}
        />
      )}
    </Layout>
  )
}
